<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';

import { Badge, BadgeDot } from '~ds/components/ui/badge';
import { Spotlight } from '~ds/components/ui/spotlight';
import { Heading, Text } from '~ds/components/ui/typography';

interface Props {
  title: string;
  description: string;
  tagLabel: string;
  illustration: string;
  illustrationTagLabel?: string;
  size?: Extract<SizeVariants, 'sm' | 'md'>;
}

const props = withDefaults(defineProps<Props>(), {
  illustrationTagLabel: undefined,
  size: 'sm',
});

const imageVariants = cva('relative', {
  variants: {
    size: {
      sm: 'lg:size-60 size-80 lg:-translate-x-4 lg:translate-y-4',
      md: 'size-80 sm:size-90 lg:-translate-x-12 lg:translate-y-2',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const imageSizes = computed(() => (props.size === 'md' ? '360px' : '240px'));
</script>

<template>
  <section
    data-testid="schools-landing-hero"
    class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2"
  >
    <div class="flex max-w-2xl flex-col items-start gap-6">
      <Badge
        tone="lime"
        class="uppercase"
        data-testid="schools-landing-hero-tag"
      >
        <BadgeDot />
        {{ tagLabel }}
      </Badge>

      <Heading
        as="h1"
        size="3xl"
        data-testid="schools-landing-hero-title"
      >
        {{ title }}
      </Heading>

      <Text
        size="sm"
        class="text-muted-foreground"
        data-testid="schools-landing-hero-description"
      >
        {{ description }}
      </Text>

      <div
        v-if="$slots.actions"
        class="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center"
        data-testid="schools-landing-hero-actions"
      >
        <slot name="actions" />
      </div>
    </div>

    <div
      class="relative flex size-96 items-center justify-center mx-auto lg:items-end lg:justify-normal lg:ml-auto lg:mr-0"
      data-testid="schools-landing-hero-illustration"
    >
      <Spotlight
        color="lime"
        size="lg"
        position="center"
      />
      <div :class="imageVariants({ size })">
        <!-- Absolute because NuxtPicture wraps the image in an inline `<picture>`, which would
             otherwise give `size-full` no height to resolve against. -->
        <NuxtPicture
          format="avif,webp"
          :src="illustration"
          :sizes="imageSizes"
          :img-attrs="{ alt: '', class: 'absolute inset-0 size-full object-contain' }"
        />
        <Badge
          v-if="illustrationTagLabel"
          variant="soft"
          tone="neutral"
          class="absolute right-2 top-[25%] uppercase shadow-sm"
          data-testid="schools-landing-hero-illustration-tag"
        >
          <BadgeDot />
          {{ illustrationTagLabel }}
        </Badge>
      </div>
    </div>
  </section>
</template>
